import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FilterOption } from "@/components/Shared/FilterGroup/FilterGroup";
import { useContracts } from "../../../networking/contract.hooks";
import { useOrganisation } from "../../../networking/organisation.hooks";
import { useSubsidiaries } from "../../../networking/subsidiary.hooks";
import { NO_SUBSIDIARY } from "../../../models/Subsidiary";

export interface InvoiceBatchFilterOptions {
	pools: FilterOption[];
	subsidiaries: FilterOption[];
	tags: FilterOption[];
	contracts: FilterOption[];
}

/**
 * Options for the invoice batch customer filter.
 *
 * Sourced entirely from the org taxonomies (pools, customer tag types),
 * the subsidiary list and the contract list — deliberately NOT derived from the
 * customer list the way `useCustomerFilterData` does it. The batch form has no
 * use for customer rows, and deriving options from them would mean loading
 * every customer just to populate four dropdowns.
 *
 * A consequence worth knowing: options are offered even when no customer
 * currently carries them, so a filter can legitimately match nothing.
 */
export function useInvoiceBatchFilterOptions(): InvoiceBatchFilterOptions & {
	isLoading: boolean;
} {
	const { t } = useTranslation();
	const { data: organisation, isLoading: isLoadingOrg } = useOrganisation();
	const { subsidiaries, isLoading: isLoadingSubsidiaries } = useSubsidiaries();
	const { contracts, isLoading: isLoadingContracts } = useContracts({ pageSize: 10000 });

	const options = useMemo<InvoiceBatchFilterOptions>(() => {
		const byName = (a: FilterOption, b: FilterOption) => a.name.localeCompare(b.name, "sv");

		return {
			pools: (organisation?.pools ?? [])
				.map((pool) => ({ key: pool.key, name: pool.name || pool.key }))
				.sort(byName),
			// The sentinel is offered first so "connected directly to the
			// organisation" is selectable alongside the real subsidiaries
			subsidiaries: [
				...(subsidiaries.length
					? [{ key: NO_SUBSIDIARY, name: t("batchFilter.noSubsidiary") }]
					: []),
				...subsidiaries.map((s) => ({ key: s.id, name: s.name })).sort(byName),
			],
			tags: (organisation?.customerTagTypes ?? [])
				.map((tag) => ({
					key: tag.key,
					name: tag.name?.sv || tag.key,
					color: tag.color,
				}))
				.sort(byName),
			contracts: (contracts ?? [])
				.map((contract) => ({ key: contract.id, name: contract.name || contract.id }))
				.sort(byName),
		};
	}, [organisation, subsidiaries, contracts, t]);

	return {
		...options,
		isLoading: isLoadingOrg || isLoadingSubsidiaries || isLoadingContracts,
	};
}
